import React, { useState } from "react";
import { DateTime } from "luxon";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Text } from "../../BuildingBlocks/Text";
import { Padding } from "../../BuildingBlocks/Padding";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMColors } from "../../../models/CRMColors";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonTertiary } from "../../CRMButtonTertiary/CRMButtonTertiary";
import { CRMCardOutsidePopupBlank } from "../../CRMCardOutsidePopupBlank/CRMCardOutsidePopupBlank";
import { CRMInputLabelAndErrorWrapComponent } from "../../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import { CRMInputCalendarComponent } from "../../CRMInputCalendarComponent/CRMInputCalendarComponent";
import CRMTextAreaComponent from "../../CRMTextAreaComponent/CRMTextAreaComponent";
import { TCasesDeadlineChange } from "../../../../../domain/codecs/CasesDeadlineChange";

type TCRMCaseDeadlineHistoryProps = {
    label: string;
    currentDate: string | null;
    changes: Array<TCasesDeadlineChange>;
    isSubmitting: boolean;
    reasonError: boolean;
    onAdd: (deadlineDate: string, reason: string) => void;
    onDelete: (id: string) => void;
};

const formatDate = (value: string): string =>
    DateTime.fromISO(value, { zone: "utc" }).toFormat("d MMM yyyy");

// London, not UTC: an audit trail that reads an hour behind the wall clock through
// BST is worse than useless when the question is when a date actually moved.
// formatDate above stays UTC - deadline_date is a date with no time to shift.
const formatDateTime = (value: string): string =>
    DateTime.fromISO(value).setZone("Europe/London").toFormat("d MMM yyyy, HH:mm");

const setByLabel = (change: TCasesDeadlineChange): string => {
    switch (change.source) {
        case "manual":
            return change.set_by_name || "Unknown";
        case "rule":
            return "Set automatically";
        case "backfill":
            return "Before this was recorded";
    }
};

export const CRMCaseDeadlineHistory = (props: TCRMCaseDeadlineHistoryProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const [newDate, setNewDate] = useState("");
    const [reason, setReason] = useState("");
    const [showErrors, setShowErrors] = useState(false);

    const newestId = props.changes.length ? props.changes[0].id : null;

    // A date with no rows behind it is a case the rule filled before the change log
    // existed. Saying so beats an empty table, which reads as data loss.
    const hasUnrecordedHistory = props.currentDate !== null && props.changes.length === 0;

    const submit = () => {
        if (newDate === "" || reason.trim().length === 0) {
            setShowErrors(true);
            return;
        }
        props.onAdd(newDate, reason.trim());
        setIsOpen(false);
        setNewDate("");
        setReason("");
        setShowErrors(false);
    };

    return (
        <SpacingColumn spacing={CRMSpacing.SMALL}>
            <Text size={CRMFontSizes.SMALL} weight={700} color={CRMColors.NEUTRAL_0}>
                {props.label}
            </Text>

            <Text size={CRMFontSizes.MED} weight={700} color={props.currentDate === null ? CRMColors.NEUTRAL_6 : CRMColors.NEUTRAL_INK}>
                {props.currentDate === null ? "Not set" : formatDate(props.currentDate)}
            </Text>

            {props.changes.length > 0 &&
                <SpacingColumn spacing={CRMSpacing.TINY}>
                    {props.changes.map((change) => (
                        <Padding key={change.id} spacing={CRMSpacing.SMALL}>
                            {/* Four columns, 280px of them fixed. Blocks default to 500px or
                                600px and crm-block-view hides overflow-x, so a wider row drops
                                the Delete button off the edge with no scrollbar to find it. */}
                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="flex-start" childSize="85px 130px 1fr 65px">
                                <Text size={CRMFontSizes.SMALL} weight={change.id === newestId ? 700 : 400} color={CRMColors.NEUTRAL_0}>
                                    {formatDate(change.deadline_date)}
                                </Text>
                                <SpacingColumn spacing={CRMSpacing.TINY}>
                                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                                        {setByLabel(change)}
                                    </Text>
                                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4}>
                                        {formatDateTime(change.created_at)}
                                    </Text>
                                </SpacingColumn>
                                <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                                    {change.reason}
                                </Text>
                                {change.id === newestId
                                    ? <CRMButtonTertiary label="Delete" onClick={() => props.onDelete(change.id)} />
                                    : <span />
                                }
                            </SpacingRow>
                        </Padding>
                    ))}
                </SpacingColumn>
            }

            {hasUnrecordedHistory &&
                <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_4}>
                    This deadline was set before changes were recorded. Its history starts at the next change.
                </Text>
            }

            <CRMButtonPrimary
                label={`${props.currentDate === null ? "Add" : "Change"} ${props.label.toLowerCase()}`}
                onClick={() => setIsOpen(true)}
                disabled={props.isSubmitting}
            />

            <CRMCardOutsidePopupBlank isOpen={isOpen} onClose={() => setIsOpen(false)} size="regular">
                <Padding spacing={CRMSpacing.LARGE}>
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        <Text size={CRMFontSizes.MED} weight={700} color={CRMColors.PRIMARY_0}>
                            {props.currentDate === null ? `Add ${props.label.toLowerCase()}` : `Change ${props.label.toLowerCase()}`}
                        </Text>

                        {props.currentDate !== null &&
                            <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                                {`Current: ${formatDate(props.currentDate)}. Adding a new one makes it the deadline. The old one stays in the history.`}
                            </Text>
                        }

                        <CRMInputLabelAndErrorWrapComponent
                            label="New deadline date"
                            displayError={showErrors && newDate === ""}
                            errorMessage="Pick a date."
                        >
                            <CRMInputCalendarComponent
                                dateType="date"
                                value={newDate}
                                onChange={setNewDate}
                                onPressEnterKey={submit}
                                displayError={showErrors && newDate === ""}
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        <CRMInputLabelAndErrorWrapComponent
                            label="Why is it changing?"
                            displayError={(showErrors && reason.trim().length === 0) || props.reasonError}
                            errorMessage="Give a reason. This is a date the other side has been given."
                        >
                            <CRMTextAreaComponent
                                value={reason}
                                onChange={setReason}
                                displayError={(showErrors && reason.trim().length === 0) || props.reasonError}
                                placeholder="e.g. Extension agreed with the executor, buyer's mortgage offer delayed."
                            />
                        </CRMInputLabelAndErrorWrapComponent>

                        <SpacingRow spacing={CRMSpacing.MEDIUM} justifyContent="end">
                            <CRMButtonTertiary label="Cancel" onClick={() => setIsOpen(false)} />
                            <CRMButtonPrimary label="Save deadline" onClick={submit} disabled={props.isSubmitting} />
                        </SpacingRow>
                    </SpacingColumn>
                </Padding>
            </CRMCardOutsidePopupBlank>
        </SpacingColumn>
    );
};
